/**
 * EVO — ta'minotchi ma'lumot qatlami (`/api/suppliers`).
 *
 * ============================================================================
 * NIMA UCHUN `lib/procurement.ts` GA QO'SHILMADI
 * ============================================================================
 * U yerda `SupplierRow` allaqachon bor, lekin u ATAYLAB TOR: xarid buyurtmasi
 * formasidagi TANLAGICH uchun yozilgan va faqat `id/code/name/country/
 * defaultCurrency/isActive` ni biladi. Kartochka esa `taxId`, kontaktlar va
 * biriktirilgan SKU larni talab qiladi.
 *
 * Tanlagichning tipini kengaytirish rad etildi: u `PurchaseOrderNew` da
 * `<option>` chizadi va u yerga kartochka maydonlari kirib kelishi hech narsa
 * bermaydi, lekin har ikkala ekranni bitta tipga bog'lab qo'yardi. Ikkita tip
 * — ikkita MAQSAD, va ikkalasi ham bir xil endpointdan o'qiydi.
 *
 * ============================================================================
 * IXTIYORIY MAYDON = RUXSAT YO'Q, «BO'SH QIYMAT» EMAS
 * ============================================================================
 * `suppliers.service.ts` dagi `supplierColumns()` `supplier.view` bo'lmasa
 * tijorat ustunlarini so'rovga UMUMAN qo'shmaydi — javobda kalit ham qolmaydi.
 * Shuning uchun bu yerda ular `?:` bilan e'lon qilingan va ekran ularni «—»
 * bilan to'ldirmaydi: ustun butunlay chizilmaydi (`routes/partners/suppliers.tsx`).
 *
 * JONLI TEKSHIRILGAN (bugun, 3000-port):
 *   admin  → `{... "defaultCurrency":"CNY","paymentTermDays":0,"leadTimeDays":135 ...}`
 *   ombor  → `{"id":1,"code":"CN-LED-379051","name":"Shenzhen LED Factory",
 *             "country":"CN","address":null,"phone":null,"email":null,"isActive":true}`
 *   sotuvchi → 403 «Bu amal uchun ruxsatingiz yo'q»
 */
import { api } from './api';
import type { Page, SortDir } from './products';

/** `suppliers.dto.ts` → `CreateSupplierSchema.defaultCurrency`. */
export const SUPPLIER_CURRENCIES = ['USD', 'CNY', 'UZS'] as const;
export type SupplierCurrency = (typeof SUPPLIER_CURRENCIES)[number];

/**
 * Ta'minotchi kontakti — `contacts.service.ts` proyeksiyasidan.
 *
 * Bu yagona blok bo'lib, u `supplier.contact.view` bilan ham keladi: konteyner
 * kelganda omborchi «kimga qo'ng'iroq qilaman?» degan savolga javob olishi
 * kerak (`suppliers.service.ts` → `SupplierDetail.contacts` izohi).
 */
export interface SupplierContact {
  readonly id: number;
  readonly fullName: string;
  readonly position: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly isPrimary: boolean;
}

/**
 * Biriktirilgan SKU.
 *
 * `lastPriceAmount` `PROTECTED_FIELDS` da (`supplier.view`) — ya'ni maydon
 * ikki qatlamda himoyalangan: so'rov proyeksiyasi va javob serializatsiyasi.
 * Butun `supplierProducts` to'plami esa `PROTECTED_COLLECTIONS` da.
 */
export interface SupplierProduct {
  readonly id: number;
  readonly productId: number;
  readonly supplierSku: string | null;
  /** Zavod qabul qiladigan eng kam partiya. Miqdor — SATR. */
  readonly moq: string | null;
  /** SKU darajasidagi lead time. `null` — ta'minotchining umumiysi ishlaydi. */
  readonly leadTimeDays: number | null;
  readonly lastPriceAmount?: string | null;
  readonly lastPriceCurrency?: string | null;
  readonly lastPriceDate?: string | null;
  readonly defectRatePct?: string | null;
}

/** Har doim keladigan maydonlar — `supplier.contact.view` ham shularni ko'radi. */
export interface SupplierBase {
  readonly id: number;
  readonly code: string;
  readonly name: string;
  readonly country: string | null;
  readonly address: string | null;
  readonly phone: string | null;
  readonly email: string | null;
  readonly isActive: boolean;
}

/**
 * KONTRAGENT TURI — 0054.
 *
 *   `goods`     — zavod / tovar yetkazib beruvchi (default, mavjud qatorlar)
 *   `logistics` — ekspeditor, tashuvchi, kema agenti
 *   `service`   — boshqa xizmat
 *
 * Egasining talabi: «Logistika kompaniyasi ham har xil holatda qarzga
 * ishlaymiz, doimiy nasiya bo'ladi... logistikaning uchoti yuritiladi.»
 *
 * ⚠️ Bu FILTR va HISOBOT o'lchovi, RUXSAT emas: CIF shartida yo'lkirani
 * zavodning o'zi hisoblaydi, ekspeditordan esa palet sotib olinadi — server
 * hech qaysi turga qattiq to'siq qo'ymaydi.
 */
export const SUPPLIER_KINDS = ['goods', 'logistics', 'service'] as const;
export type SupplierKind = (typeof SUPPLIER_KINDS)[number];

/** `supplier.view` qatlami — maydonlar IXTIYORIY (fayl boshidagi izoh). */
export interface Supplier extends SupplierBase {
  /**
   * Tur — serverning TOR oq ro'yxatida YO'Q (`SUPPLIER_PUBLIC_COLUMNS`),
   * ya'ni `supplier.view` bo'lmasa kalit umuman kelmaydi.
   */
  readonly kind?: SupplierKind;
  readonly taxId?: string | null;
  readonly defaultCurrency?: string;
  readonly paymentTermDays?: number;
  readonly leadTimeDays?: number;
  readonly createdAt?: string;
  readonly updatedAt?: string;
}

export interface SupplierDetail extends Supplier {
  /** `supplier.view` bo'lmasa kalit UMUMAN kelmaydi — shuning uchun `?:`. */
  readonly supplierProducts?: readonly SupplierProduct[];
  readonly contacts: readonly SupplierContact[];
}

export interface SupplierListParams {
  readonly page: number;
  readonly pageSize: number;
  readonly sortBy?: string | undefined;
  readonly sortDir: SortDir;
  readonly q?: string | undefined;
  readonly country?: string | undefined;
  /**
   * Tur bo'yicha filtr (0054): xarajat kiritish ekrani `logistics` bilan,
   * xarid buyurtmasi esa `goods` bilan so'raydi. Aralashgan ro'yxat
   * operatorni noto'g'ri kontragentni tanlashga olib kelardi va qarz
   * boshqa firmaga JIMGINA yozilardi.
   */
  readonly kind?: SupplierKind | undefined;
  readonly isActive?: boolean | undefined;
}

/**
 * Saralash maydonlari — RUXSATGA BOG'LIQ.
 *
 * `leadTimeDays` va `paymentTermDays` serverning tor oq ro'yxatida YO'Q
 * (`SUPPLIER_PUBLIC_SORTABLE`), chunki TARTIBNING O'ZI tijorat shartini
 * oshkor qiladi (MATRIX §4.3). Jonli tekshirilgan: `ombor@evo.uz` uchun
 * `?sortBy=leadTimeDays` → 400 «Saralash maydoni noto'g'ri».
 *
 * Shuning uchun ekran bu ikkita ustunni FAQAT `supplier.view` bo'lganda
 * saraladigan qilib chizadi — aks holda foydalanuvchi sarlavhani bosib
 * jadvalni xato holatiga tushirardi.
 */
export const SUPPLIER_SORTABLE_PUBLIC = ['code', 'name', 'country'] as const;
export const SUPPLIER_SORTABLE_FULL = [
  ...SUPPLIER_SORTABLE_PUBLIC,
  'leadTimeDays',
  'paymentTermDays',
] as const;

export const supplierKeys = {
  all: ['suppliers'] as const,
  list: (params: SupplierListParams) => ['suppliers', 'list', params] as const,
  detail: (id: number) => ['suppliers', 'detail', id] as const,
};

export function fetchSupplierList(
  params: SupplierListParams,
  signal?: AbortSignal,
): Promise<Page<Supplier>> {
  return api.get<Page<Supplier>>('/suppliers', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortBy: params.sortBy,
      sortDir: params.sortDir,
      q: params.q,
      country: params.country,
      kind: params.kind,
      // `undefined` — «filtrsiz», `false` — «faqat arxivlanganlar». Ikkalasi
      // boshqa savol, shuning uchun `?? false` yozilmaydi.
      isActive: params.isActive,
    },
    ...(signal ? { signal } : {}),
  });
}

export function fetchSupplier(id: number, signal?: AbortSignal): Promise<SupplierDetail> {
  return api.get<SupplierDetail>(`/suppliers/${id}`, { ...(signal ? { signal } : {}) });
}

/**
 * Yangi ta'minotchi — `supplier.create`.
 *
 * `defaultCurrency` va `leadTimeDays` uchun formada standart qiymat BOR
 * (`CNY`, 120), chunki server ham aynan shularni qo'yadi (`CreateSupplierSchema`).
 * Bu `NewRunForm` dagi «bo'sh maydon = sozlamani ishlat» qoidasining teskarisi
 * va farq ongli: u yerda bo'sh qiymat KATEGORIYA sozlamasiga tushardi, bu
 * yerda esa tushadigan sozlama yo'q — faqat DTO defaulti bor va uni odam
 * ko'rib turgani ma'qul (135 kunlik lead time xato bo'lsa, uni darhol
 * to'g'rilaydi).
 */
export interface CreateSupplierBody {
  readonly code: string;
  readonly name: string;
  readonly country?: string | null;
  readonly taxId?: string | null;
  readonly address?: string | null;
  readonly phone?: string | null;
  readonly email?: string | null;
  /** Berilmasa server `goods` qo'yadi. */
  readonly kind?: SupplierKind;
  readonly defaultCurrency?: SupplierCurrency;
  /**
   * «DOIMIY NASIYA» — 0054 dan boshlab bu raqam HAQIQATDA ishlaydi.
   *
   * Invoysda `dueDate` berilmasa, server uni `invoiceDate + paymentTermDays`
   * bilan to'ldiradi. Bugungacha ustun bor edi, ta'siri yo'q edi.
   */
  readonly paymentTermDays?: number;
  readonly leadTimeDays?: number;
}

/** `code` YO'Q: u PO va proformalarda qolib ketgan bo'ladi (`UpdateSupplierSchema`). */
export interface UpdateSupplierBody {
  readonly name?: string;
  readonly country?: string | null;
  readonly taxId?: string | null;
  readonly address?: string | null;
  readonly phone?: string | null;
  readonly email?: string | null;
  readonly kind?: SupplierKind;
  readonly defaultCurrency?: SupplierCurrency;
  readonly paymentTermDays?: number;
  readonly leadTimeDays?: number;
  readonly isActive?: boolean;
}

export function createSupplier(body: CreateSupplierBody): Promise<Supplier> {
  return api.post<Supplier>('/suppliers', body);
}

export function updateSupplier(id: number, body: UpdateSupplierBody): Promise<Supplier> {
  return api.patch<Supplier>(`/suppliers/${id}`, body);
}
